import { extend, pluck, isEmpty, flatten, all, size, filter, findIndex, isString } from 'underscore';
import Promise from 'bluebird';
import React from 'react';
import ReactDOM from 'react-dom';

import { AdCustomizerPickerEditorContainer } from './picker-editor-container';
import { displayLength } from './string-utils';
import { createActivity } from './instrumentation';

/** controller for ad customizer analyze
  it is a single instance and the main function is :
    .register(priority, analyzer)  -->for analyzer to register itself
    .analyzeInput({text, cursorIndex, isSelectedByUser, updateAction, target, adType})
      --> analyze input and update the picker accordingly
    .getCharCountExcludingAdCustomizers(text) -->get chart count excluding rules
    .evaluateAdCustomizers(text) --> get the preview text
 */
export class AdCustomizerController {
  constructor(props) {
    this.analyzers = [];
    this.pickerContainer = null;

    this.clickedAway = this.clickedAway.bind(this);
    this.keydown = this.keydown.bind(this);
    this.target = null;
    this.container = null;
    this.instrumentation = props.instrumentation;
  }
  /** Registers an analyzer and then ordering according to its priority
   * update target & close the picker after editor closes
  * @param {number} priority - to order menu items in ascending order
  * @param {Object} analyzer - the analyzer to use
  * @returns {Object} null
  */
  register(priority, analyzer) {
    // order by priority asc
    this.analyzers.push({ priority, analyzer });
    this.analyzers.sort((b, a) => b.priority - a.priority);
  }

  /**
   * getAnalyzerByIndex
   * @private
   * @param {Array} analyzers - analyzers array
   * @param {number} index - analyzer index
   * @returns {Object} analyzer
   */
  getAnalyzerByIndex(analyzers, index) {
    return analyzers[index].analyzer;
  }

  getAnalyzerByName(analyzers, name) {
    const analyzerIndex = findIndex(
      analyzers,
      ({ analyzer: { name: analyzerName } }) => analyzerName === name
    );
    if (analyzerIndex === -1) {
      throw new Error(`Ad customizer syntax analyzer with name '${name}' could not be found`);
    }

    return {
      analyzer: analyzers[analyzerIndex].analyzer,
      analyzerIndex,
    };
  }

  /* analyze input
    run analyzerInput for all of analyzers
    each analyzer return result format of
      {
        itemsToDisplay: {
          name, // Title of the menu item
          description: string, // description line for the menu item
          onClick: func, // menu item's click handler
        }
        type: string,
        defaultHandlerForInput: true/false,
        // means in senario when no items to display, is this still default processor
      }

    then combine those result and generate result with format of:
    {
      fallBackResult,
      hitResults, // if in-progress or compolete expression detected by analyzer
      fallBackAnalyzer
   } */
  analyzeInput({
    analyzers, text, cursorIndex, isSelectedByUser, analyzerName,
    updateAction, setCursorPostion, target, adType, field,
  }) {
    const hitResults = [];
    let fallBackAnalyzer = null;
    let fallBackResult = null;
    const pipeNextEvaluator = (previewPromise, currentPosition, resolve) => {
      previewPromise.then((result) => {
        // Analyzer the result of current analyzer
        if (result && (result.match || !isEmpty(result.itemsToDisplay))) {
          hitResults.push(extend({ analyzerId: currentPosition - 1 }, result));
        } else if (!fallBackAnalyzer && result && result.defaultHandlerForInput) {
          fallBackAnalyzer = this.getAnalyzerByIndex(analyzers, currentPosition - 1);
          fallBackResult = result;
        }

        if (analyzerName || currentPosition >= analyzers.length) {
          // Done with all analyzers, when either "sticking" to single named
          // analyzer or already went through all of them
          resolve({
            fallBackResult,
            hitResults,
            fallBackAnalyzer,
          });
        } else {
          // Switch to another analyzer
          const analyzer = this.getAnalyzerByIndex(analyzers, currentPosition);
          pipeNextEvaluator(
            analyzer.analyzeInput({
              text,
              cursorIndex,
              isSelectedByUser,
              updateAction,
              setCursorPostion,
              target,
              updateAndCloseCallback: this.updateAndCloseCallback(
                updateAction, setCursorPostion, target,
                analyzer, adType
              ),
              adType,
              field,
            }),
            currentPosition + 1,
            resolve
          );
        }
      });
    };

    let nextAnalyzerPosition = 1;
    let firstAnalyzer;
    if (analyzerName) {
      const { analyzer, analyzerIndex } = this.getAnalyzerByName(analyzers, analyzerName);
      firstAnalyzer = analyzer;
      nextAnalyzerPosition = analyzerIndex + 1;
    } else {
      firstAnalyzer = this.getAnalyzerByIndex(analyzers, 0);
      nextAnalyzerPosition = 1;
    }
    return new Promise((resolve) => {
      pipeNextEvaluator(firstAnalyzer.analyzeInput({
        text,
        cursorIndex,
        isSelectedByUser,
        updateAction,
        setCursorPostion,
        target,
        updateAndCloseCallback: this.updateAndCloseCallback(
          updateAction, setCursorPostion, target,
          firstAnalyzer, adType
        ),
        adType,
        field,
      }), nextAnalyzerPosition, resolve);
    });
  }

  /** updateAndCloseCallback
   * update target & close the picker after editor closes
  * @param {func} updateAction - update call back
  * @param {func} setCursorPostion - update cursorPostion call back
  * @param {Object} target - whether this is selected option
  * @param {Object} analyzer - the analyzer to use
  * @param {Object} adType - adType
  * @returns {Object} null
  */
  updateAndCloseCallback = (updateAction, setCursorPostion, target, analyzer, adType) =>
    ({
      newValue, cursorPosition, isSelectedByUser, event, isCancel,
    }) => {
      if (!isCancel) {
        updateAction(newValue);
        setCursorPostion(cursorPosition);
        analyzer.traceUpdate(target.value, newValue, analyzer.name, adType);

        // Setting the "sticky" flags regarding the user actions.
        // isSelectedUser is used to distinguish between the user's interactions
        // through the helper menus and the user interactions through typing
        // in a target field.
        // eslint-disable-next-line no-param-reassign
        target.isSelectedByUser = isSelectedByUser;
        // eslint-disable-next-line no-param-reassign
        target.analyzerName = analyzer.name;

        target.focus();
      }
      this.hidePicker();
      if (event) {
        event.stopPropagation();
        event.preventDefault();
      }
    };

  /**
  * OnInput update
  * got analyed result from analyzeInput
    then for the result
    -->when multiple results, just display them, each item with { name, description, onClick }
    -->when single result
      ask the analyzer whether need editor (for countdown/if/keywordinseration, it need show editor)
        --> [result 1] show editor (using the passed in container)
        --> [result 2] do nothing, then just continue will the display, with its syntax

    --> when no result, find the 1st fallback hander
    --> when no result & no default
    In the end it calls updateAction callback if there is an actual change.
  * @param {Object} options - options for parametr
  * @param {Object} options.event - event
  * @param {func} options.updateAction - update call back for Ads editor
  * @param {func} options.setCursorPostion - cursor postion call back
  *     to get updates to the text fields
  * @param {bool} options.isSelectedByUser - whether this is selected option
  *   (true - user clicked an item in the pop-up; false - user typed text)
  * @param { Object} options.container -- container to show the picker
  * @param { Object} options.remainingCountCallBack -- remaining count call back func
  * @param { Object} options.instrumentation -- instrumentation
  * @param { Object} options.adType -- adType
  * @param { Object} options.field -- field
  * @returns {Object} null
  */
  onInputUpdate({
    availableAnalyzers,
    event,
    isSelectedByUser,
    analyzerName,
    updateAction,
    setCursorPostion,
    container,
    remainingCountCallBack,
    adType,
    field,
  }) {
    const { target, key } = event;
    // selectionEnd - native prop to tell cursor position
    const { selectionEnd, value } = target;

    this.target = target;
    this.container = container;
    if (isString(this.container)) {
      this.container = document.querySelector(container);
    }

    let syntax = null;
    let items = [];
    let editorView = null;

    // for empty input, directly hide the picker
    if (isEmpty(value) || isEmpty(value.trim())) {
      return this.updatePicker({
        syntax,
        items,
        container: this.container,
        editorView,
        target,
        instrumentation: this.instrumentation,
        adType,
      });
    }

    return this.analyzeInput({
      analyzers: availableAnalyzers,
      text: value,
      cursorIndex: selectionEnd,
      isSelectedByUser,
      analyzerName,
      updateAction,
      setCursorPostion,
      target,
      remainingCountCallBack,
      adType,
      field,
    })
      .then(({
        hitResults,
        fallBackResult,
        fallBackAnalyzer,
      }) => {
        if (!isEmpty(hitResults)) {
          // Removing hits without itemsToDisplay to decide if there are multiple
          // hits with items that would need a menu as the result
          const hitResultsWithItems =
            filter(hitResults, hitResult => !isEmpty(hitResult.itemsToDisplay));
          if (size(hitResultsWithItems) > 1) {
            items = flatten(pluck(hitResultsWithItems, 'itemsToDisplay'));
          } else {
            const [analyzeResult] = size(hitResultsWithItems) > 0 ?
              hitResultsWithItems : hitResults;
            const analyzer = this.getAnalyzerByIndex(availableAnalyzers, analyzeResult.analyzerId);
            syntax = analyzer.getSyntax(analyzeResult, adType);

            if (analyzer.needEditor(analyzeResult)) {
              editorView = analyzer.getEditor({
                target,
                analyzeResult,
                remainingCountCallBack,
                key,
                updateAndCloseCallback: this.updateAndCloseCallback(
                  updateAction, setCursorPostion, target,
                  analyzer, adType
                ),
                adType,
              });
            } else {
              items = analyzeResult.itemsToDisplay;
            }
          }
        }

        // If, after looking at the results, no syntax, no items and no editor
        // were selected, we try to use fallback analyzer's syntax
        if (!syntax && isEmpty(items) && !editorView && fallBackAnalyzer) {
          syntax = fallBackAnalyzer.getSyntax(fallBackResult, adType);
        }

        return this.updatePicker({
          syntax, items, container: this.container, editorView, target, adType,
        });
      });
  }

  registerGlobalEvents(target) {
    document.addEventListener('click', this.clickedAway);
    target.addEventListener('keydown', this.keydown);
  }

  hidePicker() {
    ReactDOM.unmountComponentAtNode(this.pickerContainer);
    document.removeEventListener('click', this.clickedAway);
    this.target.removeEventListener('keydown', this.keydown);
  }

  clickedAway(event) {
    if (all(this.analyzers, ({ analyzer }) => analyzer.isClickedAway(event)) &&
      event.target !== this.target &&
      event.target.closest('.ad-customizer-container') === null) {
      this.hidePicker();
    }
  }

  /**
  * Handles key down events.
  * Hides editor when user hits escape.
  * @param {object} event - click event
  * @returns {undefined}
  */
  keydown(event) {
    switch (event.key) {
      case 'Escape':
        this.hidePicker();
        break;
      default:
        break;
    }
  }

  /**
  * getCharCountExcludingAdCustomizers
  * get chart count exluding the ad customizers
  * @param {array} analyzers - analyzers array
  * @param {string} text - text to analyze
  * @param {string} adType - ad type
  * @param {string} field - target field
  * @returns {number} count
  */
  getCharCountExcludingAdCustomizers(analyzers, text, adType, field) {
    if (!text) {
      return 0;
    }
    let resultText = text;
    let charCount = 0;
    let phaseCharCount;
    let toContinue;
    do {
      phaseCharCount = 0;
      toContinue = false;
      // Running parsers one after another, requesting evaluation of
      // a first complete expression found only, until no character contributions
      // detected
      for (let i = 0; i < analyzers.length; i += 1) {
        const analyzer = this.getAnalyzerByIndex(analyzers, i);
        const result = analyzer.getCharCountExcludingAdCustomizers(resultText, adType, field);
        resultText = result.processedText;
        phaseCharCount += result.charCount;
        toContinue = toContinue || result.toContinue;
      }
      charCount += phaseCharCount;
    } while (phaseCharCount > 0 || toContinue);

    return displayLength(resultText) + charCount;
  }

  /**
  * evaluateAdCustomizers
  * get text replacing ad customizer rules with its default text
  * @param {Array} analyzers - analyzers array
  * @param {string} text - text to analyze
  * @param {string} adType - ad type
  * @param {string} field - target field
  * @returns {Object} a promise which will resolve the final test
  */
  evaluateAdCustomizers(analyzers, text, adType) {
    if (!text) {
      return Promise.resolve(text);
    }
    const pipeNextEvaluator = (previewPromise, currentPosition, resolve) => {
      previewPromise.then((result) => {
        if (currentPosition >= analyzers.length) {
          resolve(result);
        } else {
          pipeNextEvaluator(
            this.getAnalyzerByIndex(analyzers, currentPosition).evaluateText(result, adType),
            currentPosition + 1, resolve
          );
        }
      });
    };
    return new Promise((resolve) => {
      pipeNextEvaluator(
        this.getAnalyzerByIndex(analyzers, 0).evaluateText(text, adType),
        1, resolve
      );
    });
  }

  /* Renders or updates the picker or hide it */
  // as we will use dom.unmountComponentAtNode(container), we make a specific container
  updatePicker({
    syntax, items, container, editorView, target, instrumentation, adType,
  }) {
    if (this.pickerContainer === null) {
      this.pickerContainer = document.createElement('div');
      this.pickerContainer.className = 'ad-customizer-container open';
    }

    if (container && !container.contains(this.pickerContainer)) {
      container.appendChild(this.pickerContainer);
    }

    if (!this.pickerActivity) {
      this.pickerActivity = createActivity(instrumentation, 'picker');
    }

    if (syntax || !isEmpty(items) || editorView) {
      // render latest picker
      ReactDOM.render(React.createElement(AdCustomizerPickerEditorContainer, {
        itemsToDisplay: items,
        editorView,
        syntax,
        target,
        activity: this.pickerActivity,
        container,
        adType,
      }), this.pickerContainer);
      this.registerGlobalEvents(target);
    } else {
      this.hidePicker();
    }
  }
}
